import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Mic, Star, Globe, Building, Award, Users, Compass, Briefcase, Phone,
  ChevronRight, CheckCircle, ArrowRight, Zap, Play, Volume2, VolumeX
} from 'lucide-react';
import { PurchaseButton } from '@/components/CheckoutModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DINO_VIDEO = "https://customer-assets.emergentagent.com/job_6a4505e5-3f70-4447-9214-c7fe0572ffa6/artifacts/lifcrhzx_PUBLIC%20-%20The%20Matrix%20University%20-%20MR%20Dino%20Museum%20Gym%20%281%29%20%281%29.mp4";
const SPEAKING_VIDEO = `${BACKEND_URL}/api/static/civil_rights_institute_highlight.mp4`;

const iconMap = {
  mic: Mic, star: Star, globe: Globe, building: Building, award: Award,
  users: Users, compass: Compass, briefcase: Briefcase, phone: Phone,
};

const FeaturedVideo = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-primary/20 group" data-testid="featured-video">
      <video
        ref={videoRef}
        src={DINO_VIDEO}
        className="w-full aspect-video object-cover"
        loop
        muted={muted}
        playsInline
        autoPlay
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {/* Overlay controls */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex flex-col justify-end p-4">
        <div className="flex items-end justify-between">
          <div>
            <span className="px-2 py-1 rounded bg-cta/90 text-black text-xs font-bold">MIXED REALITY</span>
            <p className="text-white font-semibold mt-2 text-sm md:text-base">Dino Museum — Gyms, Assemblies & Immersive Experiences</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setMuted(!muted)}
              className="w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
              data-testid="video-mute-btn"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={togglePlay}
              className="w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
              data-testid="video-play-btn"
            >
              {playing ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
              ) : (
                <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServiceCard = ({ service, index, featured }) => {
  const Icon = iconMap[service.icon] || Briefcase;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className={`p-6 rounded-2xl glass-light card-hover tech-bracket h-full flex flex-col ${featured ? 'ring-1 ring-cta/30' : ''}`}
      data-testid={`service-card-${service.id}`}
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-serif font-semibold text-white mb-1">{service.title}</h3>
          <span className="text-cta font-bold">{service.price}</span>
          {service.duration && <span className="text-muted-foreground text-sm ml-2">/ {service.duration}</span>}
        </div>
      </div>

      <p className="text-muted-foreground mb-5 leading-relaxed text-sm">{service.description}</p>

      {service.includes?.length > 0 && (
        <div className="mb-5 flex-1">
          <ul className="space-y-2">
            {service.includes.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <PurchaseButton
        item={service}
        itemType="service"
        className="w-full flex items-center justify-center gap-2 mt-auto"
      />
    </motion.div>
  );
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/services`).then(res => { setServices(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const coreServices = services.filter(s => s.is_featured);
  const additionalServices = services.filter(s => !s.is_featured);

  return (
    <div className="min-h-screen pt-24" data-testid="services-page">
      {/* Hero with Featured Video */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-cta font-semibold tracking-widest text-sm">EDUCATE</span>
                <span className="text-muted-foreground">&#x2022;</span>
                <span className="text-cta font-semibold tracking-widest text-sm">EXPERIENCE</span>
                <span className="text-muted-foreground">&#x2022;</span>
                <span className="text-cta font-semibold tracking-widest text-sm">INNOVATE</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-testid="services-title">
                The Matrix University Services
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Innovative education, immersive technology, esports, keynote speaking, and experiential learning programs for schools, universities, corporations, nonprofits, and special events.
              </p>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Request a Custom Quote <ChevronRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <FeaturedVideo />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      {coreServices.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="container-custom">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3">Core Services</h2>
              <p className="text-muted-foreground max-w-2xl">Our flagship offerings — from keynote presentations to full-day immersive activations.</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {coreServices.map((s, i) => <ServiceCard key={s.id} service={s} index={i} featured />)}
            </div>
          </div>
        </section>
      )}

      {/* Speaking Engagement Highlight */}
      <section className="py-12 md:py-16 bg-card/50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-primary/20 group" data-testid="speaking-video">
                <video
                  src={SPEAKING_VIDEO}
                  className="w-full aspect-video object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  poster=""
                />
                <div className="absolute top-3 left-3 pointer-events-none">
                  <span className="px-2 py-1 rounded bg-primary/90 text-white text-xs font-bold">SPEAKING ENGAGEMENT</span>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Mic className="w-10 h-10 text-cta mb-4" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4">Keynote & Speaking Engagements</h2>
              <p className="text-muted-foreground mb-4">
                Dr. Jason E. Williams, Ed.D. delivers powerful, engaging presentations at schools, conferences, corporate events, and community organizations — bringing education, technology, and innovation to every stage.
              </p>
              <p className="text-muted-foreground mb-6">
                Civil Rights Institute, Riverside CA — Education Workshop for ages 12-80.
              </p>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Book a Speaking Engagement <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      {additionalServices.length > 0 && (
        <section className="py-12 md:py-16 bg-card/50">
          <div className="container-custom">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3">Specialized Programs</h2>
              <p className="text-muted-foreground max-w-2xl">E-Sports, mobile VR labs, immersive events, media production, and more.</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {additionalServices.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Custom CTA */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center p-8 rounded-2xl glass-light">
            <Zap className="w-10 h-10 text-cta mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3">Your Vision. Our Technology.</h2>
            <p className="text-lg text-muted-foreground mb-8">
              The Matrix University can create customized experiences around your organization's goals, audience, budget, and event objectives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                Contact Us Today <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/courses" className="btn-secondary inline-flex items-center justify-center gap-2">
                View Memberships
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {loading && (
        <div className="container-custom py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="p-8 rounded-2xl glass-light animate-pulse">
                <div className="w-14 h-14 bg-muted rounded-xl mb-6" />
                <div className="h-6 bg-muted rounded mb-4" />
                <div className="h-20 bg-muted rounded mb-6" />
                <div className="h-12 bg-muted rounded" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
